import {
  Arrange,
  IconButton,
  Spacer,
  SvgBell,
  SvgMenu,
  SvgSearch,
  Tooltip,
} from '@loomhq/lens';
import cn from 'classnames';
import Mousetrap from 'mousetrap';
import React, { Suspense, lazy, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { openModal } from 'actions/modals';
import { showSearchModal } from 'actions/searchModal';
import {
  ANON_NAV_LOGIN_PAGE_CLICKED,
  GET_LOOM_CTA_CLICK,
} from 'constants/events';
import {
  EXP_INVITE_STORAGE_INCENTIVES,
  ROLLOUT_DESTINATION_SEARCH,
} from 'constants/featureFlag';
import { DESTINATION_SEARCH_MODAL } from 'constants/modalTypes';
import { MOD_K } from 'constants/mousetrap';
import { ACCOUNT_NOTIFICATIONS, SEARCH_ACCESS } from 'constants/scopes';
import { hasScope } from 'selectors/scopes';
import { selectInTrimMode } from 'selectors/shareVideo';
import { selectUserIsLogged } from 'selectors/user';

import useDowntimeBanner from 'hooks/useDowntimeBanner';
import useMedia from 'hooks/useMedia';
import useTheme from 'hooks/useTheme';
import { useFeatureFlagValue, useFlagIsActivated } from 'hooks/featureFlag';
import { useGetMemberVideoLimits } from 'hooks/workspace';

import HeaderPortalDestination from './portal-destination';
import { UpgradePrompt } from '../../profile-bubble/upgrade-prompt';

import DownloadProgress from 'components/download-progress';
import HelpBubble from 'components/help-bubble';
import HideDuringDowntime from 'components/hide-during-downtime';
import { BubbleMenu, ProfileBubb } from 'components/profile-bubble';
import Scopes from 'components/scopes';
import { useShouldShowWaveform } from 'components/share-video/edit-tools-old/hooks';
import SigninButton from 'components/signin-button';
import SignupButton from 'components/signup-button';
import GlobalContentLimitBanner from 'components/storage-incentives/global-limit-banner';
import { CompactBreadcrumb } from 'components/videos-dashboard-ta/breadcrumb';

import styles from './styles.module.less';

export const headerHeight = 64;

const Notifications = lazy(() => import('components/notifications-tray'));

const menuIcon = <SvgMenu />;

const Header = ({
  setNavBarIsOpenMobile,
  hasCompactBreadcrumb,
  showAnonNav,
}) => {
  const dispatch = useDispatch();
  const isLoggedin = useSelector(selectUserIsLogged);
  const [menu, setMenu] = React.useState(null);
  const hasSearchAccessScope = useSelector(state =>
    hasScope(state, SEARCH_ACCESS)
  );
  const inTrimMode = useSelector(selectInTrimMode);

  const shouldShowWaveform = useShouldShowWaveform();

  const searchConfig = useFeatureFlagValue(ROLLOUT_DESTINATION_SEARCH);

  const isMobile = useMedia(['(max-width: 767px)'], [true], false);

  const memberVideoLimits = useGetMemberVideoLimits();

  const hasStorageIncentive = useFlagIsActivated({
    flag: EXP_INVITE_STORAGE_INCENTIVES,
    activationValues: ['variant'],
  });

  const showVideoLimitsInHeader = useFlagIsActivated({
    flag: EXP_INVITE_STORAGE_INCENTIVES,
    activationValues: ['variant'],
  });

  function openSearch(isShortcut) {
    if (searchConfig?.modal) {
      dispatch(
        openModal(DESTINATION_SEARCH_MODAL, {
          isShortcut: Boolean(isShortcut),
        })
      );
    } else {
      dispatch(showSearchModal(isShortcut));
    }
  }

  useDowntimeBanner();

  useTheme();

  const onExit = useCallback(() => {
    setTimeout(() => {
      document.body.classList.remove('noscroll');
      setMenu(null);
    }, 150);
  }, [setMenu]);

  useEffect(() => {
    if (hasSearchAccessScope) {
      Mousetrap.bind(MOD_K, event => {
        event.preventDefault();
        openSearch(true);
      });
    }

    return () => {
      Mousetrap.unbind(MOD_K);
    };
  });

  if (!isLoggedin) {
    return (
      <>
        {!showAnonNav && <header className={styles.header} />}
        {showAnonNav && (
          <header className={styles.header}>
            <div className={styles.mobileMenu}>
              <Spacer right="medium">
                <IconButton
                  altText="Toggle library navigation"
                  icon={menuIcon}
                  onClick={() => setNavBarIsOpenMobile()}
                />
              </Spacer>
            </div>
            <div className="md:hidden">
              <HeaderPortalDestination />
            </div>
            <Spacer right="medium" />
            <Arrange justifyContent="space-between" gap="medium">
              <SigninButton
                size="medium"
                tracking={{
                  eventName: ANON_NAV_LOGIN_PAGE_CLICKED,
                  properties: {
                    from_url: window.location.href,
                    source: 'layout-header',
                  },
                }}
              />
              <SignupButton
                size="medium"
                tracking={{
                  eventName: GET_LOOM_CTA_CLICK,
                  properties: {
                    from_url: window.location.href,
                    source: 'layout-header',
                  },
                }}
              />
            </Arrange>
          </header>
        )}
      </>
    );
  }

  const isBubb = menu === 'bubb';

  const openBubbMenu = () => setMenu('bubb');

  const LoadingBubb = () => (
    <IconButton altText="Loading notifications" icon={<SvgBell />} isDisabled />
  );

  const headerContent = (
    <header className={cn(styles.header, 'layoutHeader')}>
      {inTrimMode && shouldShowWaveform && (
        <div className={styles.headerOverlay}></div>
      )}
      <DownloadProgress />
      <div className={styles.mobileMenu}>
        <Spacer right="medium">
          <IconButton
            altText="Toggle library navigation"
            icon={menuIcon}
            onClick={() => setNavBarIsOpenMobile()}
          />
        </Spacer>
      </div>
      {hasCompactBreadcrumb && (
        <HideDuringDowntime>
          <div className="sm-mr:auto">
            <CompactBreadcrumb />
          </div>
        </HideDuringDowntime>
      )}
      {showVideoLimitsInHeader && !isMobile && (
        <>
          <UpgradePrompt minimal />
          <Spacer right="medium" />
        </>
      )}

      <Arrange gap="medium">
        <HeaderPortalDestination />
      </Arrange>

      <HideDuringDowntime>
        <ul className={styles.actions}>
          <Scopes names={SEARCH_ACCESS}>
            <li>
              <Tooltip placement="bottomLeft" content="Search: ⌘ + K">
                <IconButton
                  altText="Toggle search modal"
                  icon={<SvgSearch />}
                  onClick={openSearch}
                />
              </Tooltip>
            </li>
          </Scopes>

          <Scopes names={ACCOUNT_NOTIFICATIONS}>
            <li>
              <Suspense fallback={<LoadingBubb />}>
                <Notifications />
              </Suspense>
            </li>
          </Scopes>

          <li>
            <ProfileBubb onOpen={openBubbMenu} />
          </li>
        </ul>
        {isBubb && <BubbleMenu onExit={onExit} />}
        {isLoggedin && <HelpBubble />}
      </HideDuringDowntime>
    </header>
  );

  if (
    hasStorageIncentive &&
    memberVideoLimits.remainingVideos <= 0 &&
    !isMobile
  ) {
    return (
      <GlobalContentLimitBanner memberVideoLimits={memberVideoLimits}>
        {headerContent}
      </GlobalContentLimitBanner>
    );
  }

  return headerContent;
};

export default Header;
